import { useState, useEffect, useMemo } from "react";
import { FiX, FiStar, FiCheck, FiSend } from "react-icons/fi";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { usePortalFeedback } from "../../context/PortalFeedbackContext";
import { getActiveDealerSurvey, submitDealerSurveyResponse } from "../../api/api";
import { fetchDealerProfile } from "../../redux/dealerSlice";
import type { AppDispatch } from "../../redux/store";
import type { SurveyForm, SurveyQuestion, SubmitDealerSurveyPayload, SurveyMood } from "../../types/survey.types";
import decoIcon from "../../icon.png";

// Rating scale from 1 (Dissatisfied) to 5 (Very Satisfied) with clean column alignment
const RATING_SCALE = [
  { value: 1, label: "Dissatisfied", emoji: "😞", mood: "sad" as SurveyMood },
  { value: 2, label: "Somewhat Dissatisfied", emoji: "🙁", mood: "sad" as SurveyMood },
  { value: 3, label: "Neutral", emoji: "😐", mood: "neutral" as SurveyMood },
  { value: 4, label: "Satisfied", emoji: "🙂", mood: "happy" as SurveyMood },
  { value: 5, label: "Very Satisfied", emoji: "😊", mood: "happy" as SurveyMood },
];

// Default fallback questions matching Quarterly Dealer Satisfaction Survey 2026
const DEFAULT_QUESTIONS: SurveyQuestion[] = [
  {
    id: 1,
    question: "What issues or difficulties did you experience with delivery or packaging?",
    options: [
      "Delayed shipment",
      "Damaged packaging",
      "Wrong / missing items",
      "Poor customer support",
    ],
    isRequired: true,
    order: 1,
    mood: "sad",
  },
  {
    id: 2,
    question: "How satisfied are you with product delivery speed?",
    options: [
      "Very Satisfied",
      "Satisfied",
      "Neutral",
      "Dissatisfied",
    ],
    isRequired: true,
    order: 2,
    mood: "neutral",
  },
  {
    id: 3,
    question: "Which new feature or premium product category would you like added?",
    options: [
      "TV Frames",
      "Motorized Screens",
      "Deco Mounts",
      "Sample Kits",
    ],
    isRequired: false,
    order: 3,
    mood: "happy",
  },
  {
    id: 4,
    question: "Any general feedback or suggestions for our team?",
    options: [
      "Everything is great",
      "Need better pricing",
      "Faster turnaround time",
      "More marketing collaterals",
    ],
    isRequired: false,
    order: 4,
    mood: "all",
  },
];

export default function PortalFeedbackModal() {
  const { isOpen, draft, setDraft, markSubmitted, closeFeedback } = usePortalFeedback();
  const dispatch = useDispatch<AppDispatch>();

  const [activeSurvey, setActiveSurvey] = useState<SurveyForm | null>(null);
  const [loadingSurvey, setLoadingSurvey] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);

  // Native CSS transition states
  const [show, setShow] = useState(false);
  const [render, setRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRender(true);
      setTimeout(() => setShow(true), 10);
    } else {
      setShow(false);
      setTimeout(() => setRender(false), 200);
    }
  }, [isOpen]);

  useEffect(() => {
    let isMounted = true;
    const fetchSurvey = async () => {
      if (!activeSurvey) setLoadingSurvey(true);
      try {
        const res = await getActiveDealerSurvey();
        if (isMounted && res.data && res.data.success && res.data.data) {
          setActiveSurvey(res.data.data);
        }
      } catch (err) {
        console.warn("Could not fetch active dealer survey, using fallback configuration:", err);
      } finally {
        if (isMounted) setLoadingSurvey(false);
      }
    };

    if (isOpen) {
      fetchSurvey();
    }
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const surveyId = activeSurvey?.id || 1;
  const surveyTitle = activeSurvey?.title || "Dealer Portal Feedback";
  const surveyDescription = activeSurvey?.description || "Help us make your portal experience faster and easier";
  const showRating = activeSurvey?.showRating ?? true;
  const ratingLabel = activeSurvey?.ratingLabel || "How would you rate your overall experience with the Dealer Portal?";
  const showFeedbackTextarea = activeSurvey?.showFeedbackTextarea ?? true;
  const feedbackLabel = activeSurvey?.feedbackLabel || "ACTIONABLE FEEDBACK";
  const feedbackPlaceholder = activeSurvey?.feedbackPlaceholder || "I would like it if...";
  const feedbackSubtext = activeSurvey?.feedbackSubtext || "What features, integrations, or improvements would help your business sell more frames?";
  const feedbackMaxLength = activeSurvey?.feedbackMaxLength || 500;

  const questions: SurveyQuestion[] = (activeSurvey?.questions && activeSurvey.questions.length > 0)
    ? activeSurvey.questions
    : DEFAULT_QUESTIONS;

  const handleStarClick = (rating: number) => {
    setDraft((prev) => ({ ...prev, stars: rating }));
  };

  // Helper to map star rating to mood
  const getTargetMood = (stars: number): SurveyMood => {
    if (stars <= 0) return "all";
    if (stars <= 2) return "sad";
    if (stars === 3) return "neutral";
    return "happy";
  };

  const currentMood = useMemo(() => getTargetMood(draft.stars), [draft.stars]);

  // Render ALL questions, dynamically sorted by mood (matching mood questions placed first)
  const displayedQuestions = useMemo(() => {
    if (!showRating || draft.stars === 0) {
      return [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    const getMoodPriority = (mood?: string) => {
      const m = mood || "all";
      if (m === currentMood) return 1; // Prioritize questions targeting dealer's selected rating
      if (m === "all") return 2;       // General questions
      return 3;                       // Other questions
    };

    return [...questions].sort((a, b) => {
      const priorityDiff = getMoodPriority(a.mood) - getMoodPriority(b.mood);
      if (priorityDiff !== 0) return priorityDiff;
      return (a.order ?? 0) - (b.order ?? 0);
    });
  }, [questions, showRating, draft.stars, currentMood]);

  const getQuestionKey = (q: SurveyQuestion, fallbackIndex: number) => {
    return q.id !== undefined && q.id !== null ? String(q.id) : (q.question || `q_${fallbackIndex}`);
  };

  const handleSelectAnswer = (qKey: string, option: string) => {
    setAnswers((prev) => ({
      ...prev,
      [qKey]: option,
    }));
  };

  const isFormValid = () => {
    if (showRating && draft.stars === 0) return false;
    for (let i = 0; i < displayedQuestions.length; i++) {
      const q = displayedQuestions[i];
      const key = getQuestionKey(q, i);
      if (q.isRequired && !answers[key]) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error("Please answer all required questions.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const payload: SubmitDealerSurveyPayload = {
        surveyFormId: surveyId,
        rating: showRating ? draft.stars : undefined,
        actionableFeedback: draft.comment,
        answers: displayedQuestions.map((q, i) => {
          const key = getQuestionKey(q, i);
          return {
            questionId: q.id,
            question: q.question,
            answer: answers[key] || "",
            mood: q.mood,
          };
        }),
      };
      const res = await submitDealerSurveyResponse(payload);
      if (res.data?.success) {
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
          markSubmitted();
          dispatch(fetchDealerProfile());
        }, 2000);
      } else {
        toast.error(res.data?.message || "Failed to submit survey feedback.");
      }
    } catch (err: any) {
      console.error("Error submitting dealer survey response:", err);
      toast.error(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!render) return null;

  return (
    <div
      className={`fixed inset-0 z-[999] flex items-center justify-center p-4 transition-opacity duration-200 ease-out ${
        show ? "opacity-100" : "opacity-0"
      }`}
      onClick={closeFeedback}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal Box */}
      <div
        className={`relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh] transition-transform duration-200 ease-out ${
          show ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
        }`}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "linear-gradient(145deg, #131b2e 0%, #0d1322 100%)",
          border: "1px solid rgba(176,141,85,0.25)",
        }}
      >
        {/* Header with deco icon */}
        <button onClick={closeFeedback} className="absolute top-4 right-4 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 hover:text-white flex items-center justify-center transition-colors z-10"><FiX size={15} /></button>

        <div className="px-6 pt-5 pb-4 border-b border-white/[0.08] flex items-center shrink-0 pr-12">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#B08D55]/15 flex items-center justify-center border border-[#B08D55]/30 p-1.5 shadow-inner">
              <img
                src={decoIcon}
                alt="Deco TV Frames"
                className="h-full w-auto object-contain drop-shadow"
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-white font-bold text-base tracking-tight">{surveyTitle}</h3>
              </div>
              <p className="text-gray-400 text-xs mt-0.5">{surveyDescription}</p>
            </div>
          </div>

        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1 custom-scrollbar">
          {loadingSurvey ? (
            <div className="py-16 flex flex-col items-center justify-center text-center space-y-3">
              <div className="w-7 h-7 border-2 border-[#B08D55]/30 border-t-[#B08D55] rounded-full animate-spin" />
              <p className="text-xs text-gray-400">Loading survey...</p>
            </div>
          ) : isSuccess ? (
            <div className="py-10 flex flex-col items-center justify-center text-center space-y-4 animate-in fade-in zoom-in duration-300">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                <FiCheck size={32} />
              </div>
              <div>
                <h4 className="text-lg font-bold text-white">Thank You for Your Feedback!</h4>
                <p className="text-xs text-gray-400 mt-1 max-w-xs leading-relaxed">
                  Your insights help us continuously refine tools, speed, and sales resources for all dealers.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* STEP 1: Star Rating (if enabled) - 5 aligned columns reversed 1 (Dissatisfied) to 5 (Very Satisfied) */}
              {showRating && (
                <div>
                  <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider mb-2">
                    {ratingLabel}
                  </label>

                  {/* 5-Column Aligned Rating Scale Grid: 1 (Dissatisfied) to 5 (Very Satisfied) */}
                  <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                    {RATING_SCALE.map((opt) => {
                      const isSelected = draft.stars === opt.value;
                      const isHovered = hoveredStar === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleStarClick(opt.value)}
                          onMouseEnter={() => setHoveredStar(opt.value)}
                          onMouseLeave={() => setHoveredStar(0)}
                          className={`flex flex-col items-center justify-between py-2.5 px-1 sm:px-2 rounded-xl border text-center transition-all ${
                            isSelected
                              ? "bg-[#B08D55]/20 border-[#B08D55] text-white shadow-md shadow-[#B08D55]/20 ring-1 ring-[#B08D55] scale-[1.02]"
                              : isHovered
                              ? "bg-white/[0.08] border-white/30 text-white"
                              : "bg-white/[0.03] border-white/[0.08] text-gray-400 hover:text-gray-200 hover:bg-white/[0.06] hover:border-white/20"
                          }`}
                        >
                          <span className="text-xl sm:text-2xl mb-1 select-none">{opt.emoji}</span>
                          <div className="flex items-center gap-0.5 sm:gap-1 mb-1">
                            <span className={`text-xs font-bold ${isSelected ? "text-amber-400" : "text-gray-300"}`}>
                              {opt.value}
                            </span>
                            <FiStar
                              size={11}
                              className={isSelected ? "fill-amber-400 text-amber-400" : "text-gray-500"}
                            />
                          </div>
                          <span
                            className={`text-[9px] sm:text-[10px] leading-tight font-medium line-clamp-2 ${
                              isSelected ? "text-white font-semibold" : "text-gray-400"
                            }`}
                          >
                            {opt.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* STEP 2 & 3: Revealed after rating selected (or always if rating disabled) */}
              <div
                className={`grid transition-all duration-300 ease-out ${
                  !showRating || draft.stars > 0
                    ? "grid-rows-[1fr] opacity-100 mt-5"
                    : "grid-rows-[0fr] opacity-0 mt-0"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="space-y-5 pt-1">
                    {/* Dynamic Questions from Active Survey (Targeted by Dealer's Rating/Mood) */}
                    {displayedQuestions.map((q, qIndex) => {
                      const qKey = getQuestionKey(q, qIndex);
                      return (
                        <div key={q.id ?? qKey} className="animate-in fade-in duration-200">
                          <label className="block text-xs font-semibold text-gray-300 mb-2">
                            {q.question}
                            {q.isRequired && <span className="text-amber-500 ml-1">*</span>}
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {q.options.map((opt) => {
                              const isSelected = answers[qKey] === opt;
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  onClick={() => handleSelectAnswer(qKey, opt)}
                                  className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                                    isSelected
                                      ? "bg-[#B08D55] text-white border-[#B08D55] shadow-md shadow-[#B08D55]/25"
                                      : "bg-white/[0.04] text-gray-300 border-white/[0.08] hover:border-white/20 hover:bg-white/[0.07]"
                                  }`}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}

                    {/* Actionable Feedback Textarea (if enabled) */}
                    {showFeedbackTextarea && (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="block text-xs font-bold text-gray-300 uppercase tracking-wider">
                            {feedbackLabel}
                          </label>
                          <span className="text-[10px] text-gray-400">
                            {draft.comment.length} / {feedbackMaxLength}
                          </span>
                        </div>
                        <div className="relative">
                          <textarea
                            rows={3}
                            maxLength={feedbackMaxLength}
                            value={draft.comment}
                            onChange={(e) =>
                              setDraft((prev) => ({ ...prev, comment: e.target.value }))
                            }
                            placeholder={feedbackPlaceholder}
                            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-[#B08D55]/60 focus:ring-1 focus:ring-[#B08D55]/30 resize-none leading-relaxed"
                          />
                        </div>
                        {feedbackSubtext && (
                          <p className="text-[10px] text-gray-400 mt-1.5 leading-relaxed">
                            {feedbackSubtext}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!isSuccess && !loadingSurvey && (
          <div className="px-6 py-4 border-t border-white/[0.08] flex justify-end shrink-0 bg-black/20">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormValid()}
              className="px-5 py-2.5 rounded-xl bg-[#B08D55] hover:bg-[#c9a164] text-white text-xs font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg shadow-[#B08D55]/20"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <FiSend size={13} />
                  Submit Feedback
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
